import { Typography } from '@mui/material';
import { CuriosityNamespaceType, CuriosityTranslation, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset, formatDate } from '@procureai/ui-mui';
import { filterTruthy } from '@procureai/util';
import { head, identity, isNil, join, pipe } from 'ramda';
import { useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DemandRecommendationFormData } from '../DemandRecommendationForm/DemandRecommendationForm.schema';
import { RecommendationFormContext } from '../RecommendationFormProvider/RecommendationFormProvider.context';
import { TsrctRecommendation, TsrctRecommendationDecisionStatus } from '@/generated/gql/graphql';
import { NBSP } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { CuriosityBaseLink } from '~/components/CuriosityBaseLink/CuriosityBaseLink';

import { RecommendationItemContent } from './RecommendationItemContent';

interface RecommendationItemProps {
  recommendation: TsrctRecommendation;
  itemIndex: number;
  index: number;
  isExpanded: boolean;
}

const formatNullableDate = (date: string | null | undefined): string | null => {
  return date ? formatDate(DateFormatPreset.TWO_DIGIT, new Date(date)) : null;
};

export const RecommendationItem = ({ recommendation, itemIndex, index, isExpanded }: RecommendationItemProps) => {
  const { control } = useFormContext<DemandRecommendationFormData>();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRecommendationWidget' });
  const { isFormActive } = useContext(RecommendationFormContext);

  return (
    <Controller
      name={`demandRequestItems.${itemIndex}.recommendations.${index}`}
      control={control}
      render={({ field }) => {
        const isButtonEnabled =
          (field.value?.decisionStatus === TsrctRecommendationDecisionStatus.ACCEPTED && field.value?.isDirty) ||
          field.value?.decisionStatus !== TsrctRecommendationDecisionStatus.ACCEPTED;
        return (
          <CuriosityValue
            ns={CuriosityNamespaceType.CONTRACT_ITEM}
            i18nKey={recommendation.recommendedItem}
            valueGetter={identity}
          >
            {(contractItem) => {
              if (isNil(contractItem)) {
                return null;
              }

              const resolvedContractItem = contractItem as CuriosityTranslation;
              const validFrom = formatNullableDate(resolvedContractItem.entity.contract?.validFrom);
              const validTo = formatNullableDate(resolvedContractItem.entity.contract?.validTo);

              const supplierId = resolvedContractItem.entity.contract?.supplierId;

              const { ref, ...fieldProps } = field;
              return (
                <RecommendationItemContent
                  {...fieldProps}
                  title={t('contractRecommendation', {
                    title: resolvedContractItem.value,
                  })}
                  description={resolvedContractItem.entity.description}
                  isActive={isFormActive && isExpanded && isButtonEnabled}
                  links={
                    <>
                      <Typography variant="caption" color="textPrimary">
                        <CuriosityBaseLink
                          ns={CuriosityNamespaceType.SUPPLIER}
                          i18nKey={supplierId}
                          render={(resolvedSupplier) => {
                            const { address, city } =
                              head<Partial<{ address: string; city: string }>>(
                                resolvedSupplier.entity.locations ?? [],
                              ) ?? {};
                            return {
                              displayValue: resolvedSupplier.value,
                              suffix: NBSP + pipe(filterTruthy, join(', '))([address, city]),
                            };
                          }}
                        />
                      </Typography>
                      <Typography variant="caption" color="textPrimary">
                        <CuriosityBaseLink
                          ns={CuriosityNamespaceType.CONTRACT_ITEM}
                          i18nKey={resolvedContractItem.key}
                          render={(resolvedContractItemValue) => ({
                            displayValue: t('contract.link', {
                              primarySourceReference: resolvedContractItemValue.entity.contract?.sourceReference,
                            }),
                            suffix:
                              NBSP +
                              t('contract.text', {
                                validFrom,
                                validTo,
                              }),
                          })}
                        />
                      </Typography>
                    </>
                  }
                />
              );
            }}
          </CuriosityValue>
        );
      }}
    />
  );
};
